// @custom — Toast notification store and hook (shadcn-style)
//
// Provides an imperative `toast()` API callable from anywhere (event handlers,
// async code, non-React modules) plus a `useToast()` hook for components that
// need to read/dismiss the current toast queue. Backed by a module-level
// reducer store so the `toast()` function does not require React context.

import { useEffect, useState } from 'react'

/** @typedef {'default' | 'destructive'} ToastVariant */

/**
 * What callers pass to `toast()`. Extra keys are forwarded to `<Toast>` by the
 * Toaster, so anything ui/toast accepts may ride along.
 * @typedef {Object} ToastOptions
 * @property {import('react').ReactNode} [title]
 * @property {import('react').ReactNode} [description]
 * @property {import('react').ReactNode} [action]
 * @property {ToastVariant} [variant]
 * @property {number} [duration] ms before auto-dismiss; `Infinity` keeps it open
 */

/** A queued toast as the store holds it. */
/** @typedef {Omit<ToastOptions, 'duration'> & { id: string, open: boolean, onOpenChange: (open: boolean) => void }} Toast */

/** @typedef {{ toasts: Toast[] }} ToastState */

/**
 * @typedef {{ type: 'ADD_TOAST', toast: Toast }
 *   | { type: 'UPDATE_TOAST', toast: Partial<Toast> & { id: string } }
 *   | { type: 'DISMISS_TOAST', toastId?: string }
 *   | { type: 'REMOVE_TOAST', toastId?: string }} ToastAction
 */

/** @typedef {(action: ToastAction) => void} ToastDispatch */

/** Handle returned by `toast()`. @typedef {{ id: string, dismiss: () => void, update: (next: Partial<Toast>) => void }} ToastHandle */

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 5000

/** @type {{ [K in ToastAction['type']]: K }} */
const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
}

let count = 0
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const toastTimeouts = new Map()

/**
 * @param {string} toastId
 * @param {ToastDispatch} dispatch
 * @param {number} [delay]
 */
function addToRemoveQueue(toastId, dispatch, delay = TOAST_REMOVE_DELAY) {
  if (toastTimeouts.has(toastId)) return
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({ type: actionTypes.REMOVE_TOAST, toastId })
  }, delay)
  toastTimeouts.set(toastId, timeout)
}

/** @param {string} toastId */
function clearRemoveTimeout(toastId) {
  const timeout = toastTimeouts.get(toastId)
  if (timeout) {
    clearTimeout(timeout)
    toastTimeouts.delete(toastId)
  }
}

/**
 * @param {ToastState} state
 * @param {ToastAction} action
 * @returns {ToastState}
 */
export function reducer(state, action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t
        ),
      }
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) return { ...state, toasts: [] }
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.toastId) }

    default:
      return state
  }
}

/** @type {Array<(state: ToastState) => void>} */
const listeners = []
/** @type {ToastState} */
let memoryState = { toasts: [] }

/** @type {ToastDispatch} */
function dispatch(action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => listener(memoryState))
}

/**
 * Queue a toast. Returns a handle to update or dismiss it.
 * @param {ToastOptions} [options]
 * @returns {ToastHandle}
 */
export function toast({ duration = TOAST_REMOVE_DELAY, ...props } = {}) {
  const id = genId()

  /** @param {Partial<Toast>} next */
  const update = (next) =>
    dispatch({ type: actionTypes.UPDATE_TOAST, toast: { ...next, id } })

  const dismiss = () => {
    clearRemoveTimeout(id)
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })
    addToRemoveQueue(id, dispatch, 200)
  }

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  if (duration !== Infinity) addToRemoveQueue(id, dispatch, duration)

  return { id, dismiss, update }
}

/** @param {import('react').ReactNode} message @param {ToastOptions} [opts] */
toast.success = (message, opts = {}) =>
  toast({ title: opts.title, description: message, variant: 'default', ...opts })

/** @param {import('react').ReactNode} message @param {ToastOptions} [opts] */
toast.error = (message, opts = {}) =>
  toast({ title: opts.title, description: message, variant: 'destructive', ...opts })

/** @param {import('react').ReactNode} message @param {ToastOptions} [opts] */
toast.info = (message, opts = {}) =>
  toast({ title: opts.title, description: message, variant: 'default', ...opts })

/**
 * Current toast queue + controls, re-rendering on every store change.
 * @returns {ToastState & { toast: typeof toast, dismiss: (toastId?: string) => void }}
 */
export function useToast() {
  const [state, setState] = useState(memoryState)

  useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) listeners.splice(index, 1)
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}
